import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { UserService } from '@lsq-frontend/angular-shared/features/user-service';
import { FundingMethod, Guid, VoidResponse } from '@lsq-frontend/remote-services/enterprise/common';
import {
    CancelInvoiceBatchRequest,
    CancelInvoiceBatchService,
    CompleteInvoiceBatchImageUploadRequest,
    CompleteInvoiceBatchImageUploadService,
    CreateInvoiceBatchRequest,
    CreateInvoiceBatchService,
    FailInvoiceBatchImageUploadRequest,
    FailInvoiceBatchImageUploadService,
    InvoiceBatchUploadMethod,
    ListInvoiceBatchUploadMethodsRequest,
    ListInvoiceBatchUploadMethodsService,
    RemoveInvoiceBatchImageRequest,
    RemoveInvoiceBatchImageService,
    SubmitInvoiceBatchRequest,
    SubmitInvoiceBatchService
} from '@lsq-frontend/remote-services/enterprise/dashboard-services';
import { Regex } from '@lsq-frontend/common-ui';
import { LsqValidators } from '@lsq-frontend/common-ui';
import { BehaviorSubject, from, merge, Observable, of as observableOf } from 'rxjs';
import { map } from 'rxjs/operators';
import { ICoverSheetForm, IInvoiceUploadMethodOption, InvoiceFileUpload } from '../invoices.models';
import Bugsnag from '@bugsnag/js';

@Injectable({ providedIn: 'root' })
export class InvoiceBatchService {
    public invoiceBatchKey: Guid;
    public selectedUploadMethodOption: IInvoiceUploadMethodOption;
    // removedMobileUploads should be tracked so they aren't added back by the polling loop.
    public removedMobileUploads: InvoiceFileUpload[] = [];
    public coverSheetFormValueSubject: BehaviorSubject<ICoverSheetForm>;
    public coverSheetFormIsValidSubject: BehaviorSubject<boolean>;
    private _coverSheetFormGroup: FormGroup;
    private _lastSubmittedInvoiceBatchKey: Guid;
    private _submitInvoiceBatchProperties = [
        {
            name: 'companyKey',
            validators: [Validators.required, Validators.pattern(Regex.Guid)]
        },
        {
            name: 'invoiceBatchKey',
            validators: [Validators.required, Validators.pattern(Regex.Guid)]
        },
        {
            name: 'invoiceBatchImageKeys',
            validators: [Validators.required]
        },
        {
            name: 'totalInvoiceAmount',
            validators: [
                LsqValidators.requiredGroup('InvoiceBatch', true),
                LsqValidators.minNumber(0)
            ]
        },
        {
            name: 'invoiceCount',
            validators: [LsqValidators.minNumber(1), LsqValidators.maxNumber(2147483647)]
        },
        {
            name: 'fundingMethod',
            validators: [Validators.required]
        },
        {
            name: 'fundingAmount',
            validators: [
                LsqValidators.requiredGroup('InvoiceBatch', false),
                LsqValidators.minNumber(0)
            ]
        },
        {
            name: 'bankAccountKey',
            type: 'number',
            validators: [LsqValidators.requiredGroup('InvoiceBatch', false), LsqValidators.key]
        }
    ];

    constructor(
        private _userService: UserService,
        private _createInvoiceBatchService: CreateInvoiceBatchService,
        private _completeInvoiceBatchImageUploadService: CompleteInvoiceBatchImageUploadService,
        private _failInvoiceBatchImageUploadService: FailInvoiceBatchImageUploadService,
        private _listInvoiceBatchUploadMethodsService: ListInvoiceBatchUploadMethodsService,
        private _removeInvoiceBatchImageService: RemoveInvoiceBatchImageService,
        private _submitInvoiceBatchService: SubmitInvoiceBatchService,
        private _cancelInvoiceBatchService: CancelInvoiceBatchService
    ) {
        this._initCoverSheetForm();
        this.coverSheetFormValueSubject = new BehaviorSubject<ICoverSheetForm>(
            this._coverSheetFormGroup.value
        );
        this.coverSheetFormIsValidSubject = new BehaviorSubject<boolean>(
            this.coverSheetFormGroup.valid
        );
    }

    public reset(): void {
        if (this.invoiceBatchKey && this.invoiceBatchKey !== this._lastSubmittedInvoiceBatchKey) {
            this._cancelInvoiceBatchService.execute(
                new CancelInvoiceBatchRequest({
                    invoiceBatchKey: this.invoiceBatchKey
                })
            );
        }
        delete this.invoiceBatchKey;
        this.removedMobileUploads = [];
    }

    private _initCoverSheetForm(): void {
        this._coverSheetFormGroup = new FormGroup(
            {
                totalAmount: new FormControl('', [
                    Validators.required,
                    LsqValidators.currencyPositive
                ]),
                count: new FormControl('', [
                    ...this._getValidatorsFromEndpointProp('invoiceCount'),
                    Validators.max(100000)
                ]), // Max to prevent errors, does not come from any specific req.
                fundingMethod: new FormControl(
                    '',
                    this._getValidatorsFromEndpointProp('fundingMethod')
                ),
                bankAccountKeyForDisbursement: new FormControl(null),
                customFundingAmount: new FormControl(
                    '',
                    this._getValidatorsFromEndpointProp('fundingAmount')
                )
            },
            // This group validator ensures that if `fundingMethod` is set to disburse,
            // `bankAccountKeyForDisbursement` must be set.
            (formGroup: AbstractControl) => {
                if (
                    formGroup.get('fundingMethod').value !== FundingMethod.NoDisbursement &&
                    formGroup.get('bankAccountKeyForDisbursement').value == null
                ) {
                    return {
                        bankAccountKeyForDisbursement:
                            'The bank account key is required if a disbursement funding method is selected.'
                    };
                }
                return null;
            }
        );

        this._coverSheetFormGroup.get('fundingMethod').valueChanges.subscribe((fundingMethod) => {
            if (fundingMethod === FundingMethod.SpecificAmount) {
                this._coverSheetFormGroup
                    .get('customFundingAmount')
                    .setValidators([
                        ...this._getValidatorsFromEndpointProp('fundingAmount'),
                        Validators.required
                    ]);
                this._coverSheetFormGroup.get('customFundingAmount').reset();
            } else {
                this._coverSheetFormGroup
                    .get('customFundingAmount')
                    .setValidators(this._getValidatorsFromEndpointProp('fundingAmount'));
                this._coverSheetFormGroup.get('customFundingAmount').reset();
            }
        });

        merge(
            this._coverSheetFormGroup.valueChanges,
            this._coverSheetFormGroup.statusChanges.pipe(map(() => this._coverSheetFormGroup.value))
        ).subscribe((formValue: ICoverSheetForm) => {
            this.coverSheetFormValueSubject.next(formValue);
            this.coverSheetFormIsValidSubject.next(this._coverSheetFormGroup.valid);
        });
    }

    public get coverSheetFormGroup(): FormGroup {
        return this._coverSheetFormGroup;
    }

    public get coverSheetFormValue(): ICoverSheetForm {
        return !!this._coverSheetFormGroup ? this._coverSheetFormGroup.value : null;
    }

    public getAvailableUploadMethodOptions(
        uploadMethodOptions: IInvoiceUploadMethodOption[]
    ): Promise<IInvoiceUploadMethodOption[]> {
        if (this._userService.getCompanyKey()) {
            const request = new ListInvoiceBatchUploadMethodsRequest();
            return from(this._listInvoiceBatchUploadMethodsService.executeOnce(request))
                .pipe(
                    map((response) =>
                        response.rows.reduce<IInvoiceUploadMethodOption[]>(
                            (options, batchMethod) => {
                                const option = uploadMethodOptions.find(
                                    (x) => x.method === batchMethod
                                );
                                if (!!option) {
                                    options.push(option);
                                }
                                return options;
                            },
                            []
                        )
                    )
                )
                .toPromise();
        }
        return Promise.resolve([]);
    }

    public selectUploadMethodOption(uploadMethodOption: IInvoiceUploadMethodOption): void {
        // If we're changing the selected upload method, reset the service.
        if (
            this.selectedUploadMethodOption &&
            this.selectedUploadMethodOption.method !== uploadMethodOption.method
        ) {
            this.reset();
        }
        this.selectedUploadMethodOption = uploadMethodOption;
    }

    /**
     * Streams the `invoiceBatchKey` (only once) of either the existing invoice batch or a newly-created
     * invoice batch.
     */
    public findOrCreateInvoiceBatch(masterInvoiceBatchKey?: Guid): Observable<Guid> {
        if (masterInvoiceBatchKey != null) {
            return from(this.createInvoiceBatch(masterInvoiceBatchKey));
        } else {
            if (this.invoiceBatchKey) {
                return observableOf(this.invoiceBatchKey);
            } else {
                return from(this.createInvoiceBatch());
            }
        }
    }

    public async createInvoiceBatch(masterInvoiceBatchKey?: Guid): Promise<Guid> {
        let createInvoiceBatchRequest: CreateInvoiceBatchRequest;
        const isLinkedBatch = !!masterInvoiceBatchKey;

        if (!isLinkedBatch && typeof this.invoiceBatchKey !== 'undefined') {
            throw new Error('Attempted to create an invoice batch more than once.');
        }

        if (!this._userService.getCompanyKey()) {
            return null;
        }

        if (!isLinkedBatch) {
            createInvoiceBatchRequest = new CreateInvoiceBatchRequest({
                invoiceBatchType: this.selectedUploadMethodOption.method
            });
        } else {
            createInvoiceBatchRequest = new CreateInvoiceBatchRequest({
                masterInvoiceBatchKey,
                invoiceBatchType: InvoiceBatchUploadMethod.InvoiceAndBackupSeparateUpload
            });
        }

        const createInvoiceBatchResponse = await this._createInvoiceBatchService.executeOnce(
            createInvoiceBatchRequest
        );

        if (!isLinkedBatch) {
            if (!this.invoiceBatchKey) {
                // There are use cases we aren't able to reproduce where this endpoint is getting called twice in succession.
                // This conditional will protect against batch key mismatches.
                this.invoiceBatchKey = createInvoiceBatchResponse.invoiceBatchKey;
            }
            return this.invoiceBatchKey;
        } else {
            return createInvoiceBatchResponse.invoiceBatchKey;
        }
    }

    public async removeFileFromInvoiceBatch(
        fileUpload: InvoiceFileUpload,
        invoiceBatchKey?: Guid
    ): Promise<VoidResponse> {
        fileUpload.markForRemoval();
        if (fileUpload.isMobileUpload) {
            this.removedMobileUploads.push(fileUpload);
        }
        return this._removeImageFromBatch(fileUpload.imageKey, invoiceBatchKey);
    }

    public async reportCompletedUpload(fileUpload: InvoiceFileUpload): Promise<VoidResponse> {
        // Tell the backend that the file upload is complete.
        if (this._userService.getCompanyKey()) {
            const pages = fileUpload.pages;

            try {
                const response = await this._completeInvoiceBatchImageUploadService.executeOnce(
                    new CompleteInvoiceBatchImageUploadRequest({
                        invoiceBatchImageKey: fileUpload.imageKey,
                        pages: pages != null ? pages : 1
                    })
                );
                fileUpload.isUploadStatusComplete = true;
                return response;
            } catch (error) {
                fileUpload.isUploadStatusComplete = false;
                fileUpload.reject(error);
                Bugsnag.notify(error);
            }
        }
        return null;
    }

    public reportFailedUpload(fileUpload: InvoiceFileUpload): void {
        const invoiceBatchImageKey = fileUpload.imageKey;
        if (invoiceBatchImageKey && this._userService.getCompanyKey()) {
            // Tell the backend that the file upload failed.
            this._failInvoiceBatchImageUploadService.executeOnce(
                new FailInvoiceBatchImageUploadRequest({
                    invoiceBatchImageKey
                })
            );
        }
    }

    public async submit(
        invoiceBatchImageKeys: Guid[],
        invoiceBatchKey?: Guid,
        isLinkedBatch = false
    ): Promise<VoidResponse> {
        const submitInvoiceBatchRequest = new SubmitInvoiceBatchRequest({
            fundingMethod: FundingMethod.AdditionalUpload,
            invoiceBatchKey: invoiceBatchKey || this.invoiceBatchKey,
            invoiceBatchImageKeys
        });

        if (!isLinkedBatch) {
            submitInvoiceBatchRequest.fundingMethod = this.coverSheetFormValue.fundingMethod;
            submitInvoiceBatchRequest.totalInvoiceAmount = this.coverSheetFormValue.totalAmount;
            submitInvoiceBatchRequest.invoiceCount = this.coverSheetFormValue.count;
            submitInvoiceBatchRequest.fundingAmount = this.coverSheetFormValue.customFundingAmount;
            submitInvoiceBatchRequest.bankAccountKey =
                this.coverSheetFormValue.bankAccountKeyForDisbursement;
        }

        this._lastSubmittedInvoiceBatchKey = submitInvoiceBatchRequest.invoiceBatchKey;
        return this._submitInvoiceBatchService.executeOnce(submitInvoiceBatchRequest);
    }

    // Boolean methods.
    public isSpreadsheetUploadMethodSelected(): boolean {
        return (
            this.selectedUploadMethodOption &&
            this.selectedUploadMethodOption.method ===
                InvoiceBatchUploadMethod.SpreadsheetAndBackupUpload
        );
    }

    public isCombinedImagesUploadMethodSelected(): boolean {
        return (
            this.selectedUploadMethodOption &&
            this.selectedUploadMethodOption.method ===
                InvoiceBatchUploadMethod.InvoiceAndBackupCombinedUpload
        );
    }

    public allowMultiFileUpload(parentFileUpload?: InvoiceFileUpload): boolean {
        return !!parentFileUpload || !this.isSpreadsheetUploadMethodSelected();
    }

    private async _removeImageFromBatch(
        imageFileKey: Guid,
        invoiceBatchKey: Guid
    ): Promise<VoidResponse> {
        return this._removeInvoiceBatchImageService.executeOnce(
            new RemoveInvoiceBatchImageRequest({
                invoiceBatchKey: invoiceBatchKey == null ? this.invoiceBatchKey : invoiceBatchKey,
                imageFileKey
            })
        );
    }

    private _getValidatorsFromEndpointProp(propertyKey: string): any[] {
        return this._submitInvoiceBatchProperties.find((property) => property.name === propertyKey)
            .validators;
    }
}
